import axios from 'axios';
import { apiCache, offlineQueue } from '../utils/offlineStorage';
import rateLimitHandler from '../utils/rateLimitHandler';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://admin.tinatacademy.com/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

function getCacheKey(config) {
  const { method, url, params, data } = config;
  const paramsStr = params ? JSON.stringify(params) : '';
  const dataStr = data ? JSON.stringify(data) : '';
  return `${method}:${url}:${paramsStr}:${dataStr}`;
}

// Request interceptor: auth token, offline cache, deduplication
api.interceptors.request.use(
  async (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    // Check cache for GET requests when offline
    if (config.method === 'get' && !navigator.onLine) {
      const cacheKey = getCacheKey(config);
      const cachedData = await apiCache.get(cacheKey);
      if (cachedData) {
        return Promise.reject({ isCached: true, data: cachedData, config });
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: cache, offline queue, retry on 429
api.interceptors.response.use(
  async (response) => {
    if (response.config.method === 'get' && response.data) {
      const cacheKey = getCacheKey(response.config);
      await apiCache.set(cacheKey, response.data, 24 * 60 * 60 * 1000);
    }
    return response;
  },
  async (error) => {
    if (error.isCached) {
      return Promise.resolve({
        data: error.data,
        status: 200,
        statusText: 'OK (Cached)',
        headers: {},
        config: error.config,
        isCached: true,
      });
    }

    // Rate limit: retry with exponential backoff (1 attempt)
    if (error.response?.status === 429) {
      const requestKey = rateLimitHandler.getRequestKey(
        error.config?.method,
        error.config?.url,
        error.config?.params
      );
      try {
        const response = await rateLimitHandler.executeWithRetry(
          () => api.request(error.config),
          requestKey,
          1
        );
        return response;
      } catch (retryError) {
        const retryAfter = error.response?.headers['retry-after'];
        if (retryAfter) {
          retryError.message = `Rate limit exceeded. Please wait ${retryAfter} seconds and try again.`;
        } else {
          retryError.message = 'Rate limit exceeded. Please wait a moment and try again.';
        }
        return Promise.reject(retryError);
      }
    }

    // Offline: queue mutations for later sync
    if (!navigator.onLine) {
      error.isOffline = true;
      error.message = 'You are offline. Please check your internet connection.';
      if (['post', 'put', 'patch', 'delete'].includes(error.config?.method?.toLowerCase())) {
        const queueId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        await offlineQueue.add(queueId, {
          method: error.config.method,
          url: error.config.url,
          data: error.config.data,
          headers: error.config.headers,
        });
      }
    }

    return Promise.reject(error);
  }
);

export default api;
